/**
 * Recurring-occurrence identity TOKEN — the SINGLE source of truth shared by the
 * calendar recurrence expander, the rendered occurrence chips, and the
 * agent-effects ingest path. Kept in `lib` (a pure leaf, no React / no module
 * imports) so BOTH `modules/calendar` and `app/providers` can reuse the exact
 * same token function without an upward/bidirectional import (ADR 024). This is
 * the canonical definition; `modules/calendar/recurrence.ts` re-exports it.
 *
 * Reuse — never reimplement — these to derive an occurrence key, or the receipt
 * key and the chip key will skew and the animation won't fire.
 */

/** Format a Date's UTC year/month/day as a date-only `YYYY-MM-DD` string. */
function formatDateOnlyUTC(d: Date): string {
  return d.toISOString().slice(0, 10);
}

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Normalize an occurrence start to an exception-match TOKEN, applied IDENTICALLY
 * on both sides of a match. All-day occurrences key by calendar date
 * (`d:YYYY-MM-DD`); timed occurrences key by epoch ms (`t:<ms>`).
 *
 * Collapses format/offset skew: a moved/cancelled exception is stored with
 * `originalStartAt` in local-offset form (e.g. `2026-06-29T09:00:00-07:00`) while
 * the generated occurrence is the UTC-Z instant (`2026-06-29T16:00:00.000Z`) —
 * the SAME moment, different strings. Keying timed occurrences by epoch ms makes
 * both sides collapse to one token regardless of offset/precision. Mirrors the
 * backend `startToken` in apps/api `event-expansion.ts`. Returns `null` for an
 * unparseable/empty value (guarded so a bad row can't key a bogus suppression).
 */
export function occToken(value: string | number | Date | null | undefined, allDay: boolean): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (allDay) {
    // Date-only string → use verbatim; anything else → its UTC calendar day.
    if (typeof value === 'string' && DATE_ONLY_RE.test(value)) return `d:${value}`;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : `d:${formatDateOnlyUTC(d)}`;
  }
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(ms) ? null : `t:${ms}`;
}

/**
 * Normalize a recurring occurrence's start string into the agent-effect
 * `instanceId` TOKEN used to key an occurrence chip. Wraps {@link occToken} with
 * the all-day inference BOTH sides of the match share (date-only string ⇒ all-day
 * ⇒ `d:YYYY-MM-DD`; timed ⇒ `t:<epoch ms>`), so a receipt's raw `originalStartAt`
 * and the rendered chip's `_originalStartAt`/`originalStartAt` — which differ only
 * in format/offset — collapse to the SAME token. Returns `undefined` for an
 * empty/unparseable value.
 */
export function occInstanceToken(value: string | null | undefined): string | undefined {
  if (value == null || value === '') return undefined;
  return occToken(value, DATE_ONLY_RE.test(value)) ?? undefined;
}
